import type { FetchResult } from "@apollo/client";
import { getApolloClient } from "../../../../apollo";
import type {
    GqlClientCreateEditorAvatarInput,
    GqlClientCreateEditorAvatarMutation,
    GqlClientCreateEditorAvatarMutationVariables,
    GqlClientCreateEditorFlexiblePlaceholderInput,
    GqlClientCreateEditorFlexiblePlaceholderMutation,
    GqlClientCreateEditorFlexiblePlaceholderMutationVariables,
    GqlClientCreateEditorOverlayPlaceholderInput,
    GqlClientCreateEditorOverlayPlaceholderMutation,
    GqlClientCreateEditorOverlayPlaceholderMutationVariables,
    GqlClientDeleteEditorFlexiblePlaceholderMutation,
    GqlClientDeleteEditorFlexiblePlaceholderMutationVariables,
    GqlClientDuplicateEditorFlexiblePlaceholderMutation,
    GqlClientDuplicateEditorFlexiblePlaceholderMutationVariables,
    GqlClientEditorPlaceholderFragment,
    GqlClientLocalEditorPlaceholderQuery,
    GqlClientLocalEditorPlaceholderQueryVariables,
    GqlClientReorderEditorFlexiblePlaceholdersInput,
    GqlClientReorderEditorFlexiblePlaceholdersMutation,
    GqlClientReorderEditorFlexiblePlaceholdersMutationVariables,
    GqlClientReorderEditorOverlayPlaceholdersInput,
    GqlClientReorderEditorOverlayPlaceholdersMutation,
    GqlClientReorderEditorOverlayPlaceholdersMutationVariables,
    GqlClientUpdateEditorAvatarPlaceholderInput,
    GqlClientUpdateEditorAvatarPlaceholderMutation,
    GqlClientUpdateEditorAvatarPlaceholderMutationVariables,
    GqlClientUpdateEditorPlaceholderInput,
    GqlClientUpdateEditorPlaceholderMutation,
    GqlClientUpdateEditorPlaceholderMutationVariables
} from "../../../../graphql/graphqlGeneratedTypes/graphqlClient";
import {
    CreateEditorAvatarDocument,
    CreateEditorFlexiblePlaceholderDocument,
    CreateEditorOverlayPlaceholderDocument,
    DeleteEditorFlexiblePlaceholderDocument,
    DuplicateEditorFlexiblePlaceholderDocument,
    GqlClientEditorFlexiblePlaceholderParentType,
    GqlClientUpdateEditorPlaceholderResult,
    LocalEditorPlaceholderDocument,
    ReorderEditorFlexiblePlaceholdersDocument,
    ReorderEditorOverlayPlaceholdersDocument,
    UpdateEditorAvatarPlaceholderDocument,
    UpdateEditorPlaceholderDocument
} from "../../../../graphql/graphqlGeneratedTypes/graphqlClient";
import { IGNORE_UPDATED } from "../../../../logic/common/Consts";
import { PendoService } from "../../../pendoService";
import { localEditorRaasPlaceholderData, localEditorShowPlaceholderLibrary } from "../../State";
import type { PlaceholderId, RaasPlaceholderData } from "../../types";
import { getPlaceholderOrder } from "../../Utils";
import { setIsSelectedSceneIsNew, setSelectedPlaceholderId } from "../Project";
import { getScene } from "../Scene";

export const getRaasPlaceholderData = (placeholderId: PlaceholderId): RaasPlaceholderData => {
    const defaultRaasPlaceholderData: RaasPlaceholderData = { position: {} };

    return localEditorRaasPlaceholderData()[placeholderId] || defaultRaasPlaceholderData;
};

export const setRaasPlaceholderData = (placeholderId: PlaceholderId, data: RaasPlaceholderData): void => {
    localEditorRaasPlaceholderData({
        ...localEditorRaasPlaceholderData(),
        [placeholderId]: {
            ...getRaasPlaceholderData(placeholderId),
            ...data
        }
    });
};

export const getPlaceholder = (placeholderId: PlaceholderId): GqlClientEditorPlaceholderFragment | null => {
    if (!placeholderId) {
        return null;
    }

    // We use here optimistic: true, since we use optimistic response and we want to get the optimistic data, without waiting for the
    // actual result from server to be reflected in cache.
    // There are in the cache two origins for data, optimised and actual.
    // In case there is no optimistic data we get the actual data that we have updated in the cache.
    const cachedQuery = getApolloClient().cache.readQuery<GqlClientLocalEditorPlaceholderQuery, GqlClientLocalEditorPlaceholderQueryVariables>({
        query: LocalEditorPlaceholderDocument,
        variables: { placeholderId },
        optimistic: true
    });
    return cachedQuery.localEditorPlaceholder;
};

export const updatePlaceholder = async (updatePlaceholderInput: Omit<GqlClientUpdateEditorPlaceholderInput, "updated">): Promise<void> => {
    try {
        const client = getApolloClient();
        const currentPlaceholder = getPlaceholder(updatePlaceholderInput.id);
        await client.mutate<GqlClientUpdateEditorPlaceholderMutation, GqlClientUpdateEditorPlaceholderMutationVariables>({
            mutation: UpdateEditorPlaceholderDocument,
            variables: {
                updateEditorPlaceholderInput: {
                    id: updatePlaceholderInput.id,
                    placeholderContent: updatePlaceholderInput.placeholderContent,
                    placeholderSettings: updatePlaceholderInput.placeholderSettings,
                    flexibleSettings: updatePlaceholderInput.flexibleSettings
                        ? {
                            top: updatePlaceholderInput.flexibleSettings.top,
                            left: updatePlaceholderInput.flexibleSettings.left,
                            width: updatePlaceholderInput.flexibleSettings.width,
                            height: updatePlaceholderInput.flexibleSettings.height
                        }
                        : null,
                    updated: IGNORE_UPDATED
                }
            },
            optimisticResponse: {
                __typename: "Mutation",
                updateEditorPlaceholder: {
                    __typename: "UpdateEditorPlaceholderOutputSuccess",
                    result: GqlClientUpdateEditorPlaceholderResult.SUCCESS,
                    editorPlaceholder: {
                        __typename: "EditorPlaceholder",
                        ...currentPlaceholder,
                        ...updatePlaceholderInput
                    }
                }
            }
        });
    }
    catch (e) {
        // ignore
    }
};

export const selectPlaceholder = (placeholderId: PlaceholderId) => {
    setIsSelectedSceneIsNew(false);
    setSelectedPlaceholderId(placeholderId);
};

export function createAvatar(input: GqlClientCreateEditorAvatarInput): Promise<FetchResult<GqlClientCreateEditorAvatarMutation>> {
    const client = getApolloClient();
    return client.mutate<
        GqlClientCreateEditorAvatarMutation,
        GqlClientCreateEditorAvatarMutationVariables
    >({
        mutation: CreateEditorAvatarDocument,
        variables: {
            input
        }
    });
}

export function createOverlayPlaceholder(
    input: GqlClientCreateEditorOverlayPlaceholderInput
): Promise<FetchResult<GqlClientCreateEditorOverlayPlaceholderMutation>> {
    const client = getApolloClient();
    return client.mutate<
        GqlClientCreateEditorOverlayPlaceholderMutation,
        GqlClientCreateEditorOverlayPlaceholderMutationVariables
    >({
        mutation: CreateEditorOverlayPlaceholderDocument,
        variables: {
            input
        }
    });
}

export async function deleteFlexiblePlaceholder(
    placeholderId: string,
    trackingLayoutId: string,
    trackingLayoutName: string,
    trackingPhIntentName: string,
    trackingTrigger: "Toolbar" | "Keyboard",
    extraTrackingInfo: { [key: string]: string } = {}
): Promise<void> {
    const client = getApolloClient();
    await client.mutate<
        GqlClientDeleteEditorFlexiblePlaceholderMutation,
        GqlClientDeleteEditorFlexiblePlaceholderMutationVariables
    >({
        mutation: DeleteEditorFlexiblePlaceholderDocument,
        variables: {
            input: {
                placeholderId
            }
        }
    });
    PendoService.getInstance().trackEvent(
        "PH Deleted",
        {
            deletedPhIntentName: trackingPhIntentName,
            layoutId: trackingLayoutId,
            layoutName: trackingLayoutName,
            trigger: trackingTrigger,
            ...extraTrackingInfo
        }
    );
}

export async function duplicateFlexiblePlaceholder(
    placeholderId: string,
    trackingLayoutId: string,
    trackingLayoutName: string,
    trackingPhIntentName: string,
    trackingTrigger: "toolbar" | "keyboard"
) {
    const client = getApolloClient();
    const result = await client.mutate<
        GqlClientDuplicateEditorFlexiblePlaceholderMutation,
        GqlClientDuplicateEditorFlexiblePlaceholderMutationVariables
    >({
        mutation: DuplicateEditorFlexiblePlaceholderDocument,
        variables: {
            input: {
                placeholderId
            }
        }
    });

    PendoService.getInstance().trackEvent(
        "PH added",
        {
            layoutId: trackingLayoutId,
            layoutName: trackingLayoutName,
            addedPhIntentName: trackingPhIntentName,
            isDuplicated: "true",
            trigger: trackingTrigger
        }
    );

    return result;
}

export function updateAvatarPlaceholder(
    updateAvatarPlaceholderInput: Omit<GqlClientUpdateEditorAvatarPlaceholderInput, "updated">
) {
    const client = getApolloClient();
    return client.mutate<
        GqlClientUpdateEditorAvatarPlaceholderMutation,
        GqlClientUpdateEditorAvatarPlaceholderMutationVariables
    >({
        mutation: UpdateEditorAvatarPlaceholderDocument,
        variables: {
            input: {
                id: updateAvatarPlaceholderInput.id,
                placeholderContent: updateAvatarPlaceholderInput.placeholderContent,
                placeholderSettings: updateAvatarPlaceholderInput.placeholderSettings,
                updated: IGNORE_UPDATED
            }
        }
    });
}

export function createFlexiblePlaceholder(
    input: GqlClientCreateEditorFlexiblePlaceholderInput
) {
    const client = getApolloClient();
    return client.mutate<
        GqlClientCreateEditorFlexiblePlaceholderMutation,
        GqlClientCreateEditorFlexiblePlaceholderMutationVariables
    >({
        mutation: CreateEditorFlexiblePlaceholderDocument,
        variables: {
            input
        }
    });
}

export function getShowPlaceholderLibrary(): boolean {
    return localEditorShowPlaceholderLibrary();
}

export function setShowPlaceholderLibrary(value: boolean): void {
    localEditorShowPlaceholderLibrary(value);
}

export function reorderFlexiblePlaceholders(
    input: GqlClientReorderEditorFlexiblePlaceholdersInput
) {
    const client = getApolloClient();
    return client.mutate<
        GqlClientReorderEditorFlexiblePlaceholdersMutation,
        GqlClientReorderEditorFlexiblePlaceholdersMutationVariables
    >({
        mutation: ReorderEditorFlexiblePlaceholdersDocument,
        variables: {
            input
        }
    });
}

export function reorderOverlayPlaceholders(
    input: GqlClientReorderEditorOverlayPlaceholdersInput
) {
    const client = getApolloClient();
    return client.mutate<
        GqlClientReorderEditorOverlayPlaceholdersMutation,
        GqlClientReorderEditorOverlayPlaceholdersMutationVariables
    >({
        mutation: ReorderEditorOverlayPlaceholdersDocument,
        variables: {
            input
        }
    });
}

export enum ReorderPlaceholdersAction {
    BRING_FORWARD = "BRING_FORWARD",
    BRING_TO_FRONT = "BRING_TO_FRONT",
    SEND_BACKWARD = "SEND_BACKWARD",
    SEND_TO_BACK = "SEND_TO_BACK"
}

export async function reorderPlaceholders(
    sceneId: string,
    placeholderId: string,
    action: ReorderPlaceholdersAction,
    trackingTrigger: "toolbar" | "keyboard"
): Promise<void> {
    const scene = getScene(sceneId);
    const targetPlaceholder = getPlaceholder(placeholderId);
    const placeholderOrder = getPlaceholderOrder(scene, targetPlaceholder);

    const currentIndex = placeholderOrder.indexOf(placeholderId);
    switch (action) {
        case ReorderPlaceholdersAction.BRING_FORWARD:
        case ReorderPlaceholdersAction.BRING_TO_FRONT: {
            if (currentIndex === placeholderOrder.length - 1) {
                return;
            }
            break;
        }
        case ReorderPlaceholdersAction.SEND_BACKWARD:
        case ReorderPlaceholdersAction.SEND_TO_BACK: {
            if (currentIndex === 0) {
                return;
            }
            break;
        }
    }

    const newPlaceholderOrder = [...placeholderOrder];
    switch (action) {
        case ReorderPlaceholdersAction.BRING_FORWARD: {
            [
                newPlaceholderOrder[currentIndex],
                newPlaceholderOrder[currentIndex + 1]
            ] = [
                newPlaceholderOrder[currentIndex + 1],
                newPlaceholderOrder[currentIndex]
            ];
            break;
        }
        case ReorderPlaceholdersAction.BRING_TO_FRONT: {
            newPlaceholderOrder.splice(currentIndex, 1);
            newPlaceholderOrder.push(placeholderId);
            break;
        }
        case ReorderPlaceholdersAction.SEND_BACKWARD: {
            [
                newPlaceholderOrder[currentIndex - 1],
                newPlaceholderOrder[currentIndex]
            ] = [
                newPlaceholderOrder[currentIndex],
                newPlaceholderOrder[currentIndex - 1]
            ];
            break;
        }
        case ReorderPlaceholdersAction.SEND_TO_BACK: {
            newPlaceholderOrder.splice(currentIndex, 1);
            newPlaceholderOrder.unshift(placeholderId);
        }
    }

    if (targetPlaceholder.parentType === GqlClientEditorFlexiblePlaceholderParentType.PLACEHOLDER) {
        await reorderOverlayPlaceholders({
            parentPlaceholderId: targetPlaceholder.parentPlaceholder.id,
            placeholderOrder: newPlaceholderOrder
        });
    }
    if (targetPlaceholder.parentType === GqlClientEditorFlexiblePlaceholderParentType.CANVAS) {
        await reorderFlexiblePlaceholders({
            sceneId,
            placeholderOrder: newPlaceholderOrder
        });
    }

    const newIndex = newPlaceholderOrder.indexOf(placeholderId);
    const newPhIntentUnder = newIndex > 0
        ? getPlaceholder(newPlaceholderOrder[newIndex - 1])?.ccPlaceholder.intent.name
        : null;
    const newPhIntentOver = newIndex < newPlaceholderOrder.length - 1
        ? getPlaceholder(newPlaceholderOrder[newIndex + 1])?.ccPlaceholder.intent.name
        : null;

    PendoService.getInstance().trackEvent(
        "Z order changed",
        {
            orderedPhIntent: targetPlaceholder.ccPlaceholder.intent.name,
            zOrderOption: action,
            newPhIntentUnder,
            newPhIntentOver,
            trigger: trackingTrigger
        }
    );
}
